import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

export interface Vehicle {
  id: string;
  regNumber: string;
  nickname: string | null;
  type: "CAR" | "BIKE" | "SCOOTER" | "COMMERCIAL";
  make: string | null;
  model: string | null;
  variant: string | null;
  fuel: "PETROL" | "DIESEL" | "CNG" | "EV" | "HYBRID" | null;
  year: number | null;
  purchaseDate: string | null;
  odometerKm: number | null;
  insurer: string | null;
  policyNo: string | null;
  insuranceExpiry: string | null;
  pucExpiry: string | null;
  fitnessExpiry: string | null;
  archivedAt: string | null;
}

export interface VehicleListItem extends Vehicle {
  totalSpend: string;
  serviceCount: number;
}

export const vehicleKeys = {
  all: ["vehicles"] as const,
  list: (archived: boolean) => ["vehicles", { archived }] as const,
  one: (id: string) => ["vehicles", id] as const,
};

export function useVehicles(archived = false) {
  return useQuery({
    queryKey: vehicleKeys.list(archived),
    queryFn: () =>
      api<{ vehicles: VehicleListItem[]; rcLookupAvailable: boolean }>(
        `/api/vehicles${archived ? "?archived=true" : ""}`,
      ),
  });
}

export function useVehicle(id: string) {
  return useQuery({
    queryKey: vehicleKeys.one(id),
    queryFn: () => api<{ vehicle: Vehicle }>(`/api/vehicles/${id}`),
    // The add-vehicle form calls this with "" — without the guard it would
    // fetch /api/vehicles/ and show a 404 on a page that is working fine.
    enabled: Boolean(id),
  });
}

type VehicleInput = Partial<Omit<Vehicle, "id" | "archivedAt">>;

export function useSaveVehicle(id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VehicleInput) =>
      api<{ vehicle: Vehicle }>(
        id ? `/api/vehicles/${id}` : "/api/vehicles",
        { method: id ? "PATCH" : "POST", body: input },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: vehicleKeys.all }),
  });
}

export function useArchiveVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/vehicles/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: vehicleKeys.all }),
  });
}

export function useRestoreVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/vehicles/${id}/restore`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: vehicleKeys.all }),
  });
}
